import React from "react";
import styles from "./MerchantCenterViewer.module.css";
import {
  renderParagraphs,
  splitLines,
  splitSentences,
  stripBullet,
} from "./utils.jsx";
import checkIcon from "../../../assets/icons/check-icon.png";

const LABELS = [
  "Summary",
  "Details",
  "Business Name",
  "Overall store quality",
  "Address",
  "Email",
  "Customer service contact",
  "Flagged products",
];

const splitIntoSections = (markdown = "") => {
  const lines = markdown.split(/\r?\n/);
  const sections = {};
  let currentKey = null;

  const keyForLabel = (label) => label.toLowerCase().replace(/\s+/g, "_");

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) return;

    const foundLabel = LABELS.find((lbl) => {
      const lowerLine = line.toLowerCase();
      const lowerLabel = lbl.toLowerCase();
      if (!lowerLine.startsWith(lowerLabel)) return false;
      const remainder = lowerLine.slice(lowerLabel.length);
      return (
        remainder.startsWith(":") ||
        remainder.startsWith(" score:") ||
        remainder.startsWith(" –") ||
        remainder.startsWith(" -")
      );
    });

    if (foundLabel) {
      currentKey = keyForLabel(foundLabel);
      const afterColon = line.slice(line.indexOf(":") + 1).trim();
      if (!sections[currentKey]) sections[currentKey] = "";
      if (afterColon) sections[currentKey] = afterColon;
      return;
    }

    if (currentKey) {
      sections[currentKey] = sections[currentKey]
        ? `${sections[currentKey]}\n${line}`
        : line;
    }
  });

  return sections;
};

const MerchantCenterViewer = ({ markdown }) => {
  if (!markdown) return null;

  const sections = splitIntoSections(markdown);
  if (!Object.keys(sections).length) {
    return (
      <div className={styles.card}>{renderParagraphs(markdown, styles)}</div>
    );
  }

  const summary = sections.summary || "";
  const details = sections.details || "";
  const businessName = splitLines(sections.business_name || "")[0] || "";
  const address = splitLines(sections.address || "")[0] || "";
  const email = splitLines(sections.email || "")[0] || "";
  const contact = splitLines(sections.customer_service_contact || "")[0] || "";
  const flaggedProducts = splitLines(sections.flagged_products || "").map(
    stripBullet,
  );

  const summaryPoints = splitSentences(summary);
  const detailsPoints = splitLines(details).map(stripBullet);

  // Use contact as fallback if email is not explicitly provided
  const displayEmail = email || contact;

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon}>
            <img src={checkIcon} alt="check" className={styles.checkIconImg} />
          </div>
          <h4 className={styles.heroTitle}>Trust Checklist</h4>
        </div>
        <p className={styles.heroSubtitle}>
          Trust, policy, and product quality insights
        </p>
      </div>

      {/* Business Section */}
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>BUSINESS</div>
        <div className={styles.businessRow}>
          <div className={styles.businessName}>{businessName || "—"}</div>
          <div className={styles.businessDivider} />
          <div className={styles.businessField}>
            <span className={styles.fieldLabel}>Address</span>
            <span className={styles.fieldValue}>{address || "—"}</span>
          </div>
          <div className={styles.businessDivider} />
          <div className={styles.businessField}>
            <span className={styles.fieldLabel}>Email</span>
            <span className={styles.fieldValue}>{displayEmail || "—"}</span>
          </div>
        </div>
      </div>

      {/* Analysis Section */}
      {(summaryPoints.length > 0 || detailsPoints.length > 0) && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>ANALYSIS</div>
          <div className={styles.analysisContent}>
            {summaryPoints.map((line, idx) => (
              <div className={styles.analysisItem} key={`summary-${idx}`}>
                <span className={styles.summaryCheck} />
                <span className={styles.analysisText}>{line}</span>
              </div>
            ))}
            {detailsPoints.map((line, idx) => (
              <div className={styles.analysisItem} key={`detail-${idx}`}>
                <span className={styles.summaryCheck} />
                <span className={styles.analysisText}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flagged Products Section */}
      {flaggedProducts.length > 0 && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>FLAGGED PRODUCTS</div>
          <div className={styles.analysisContent}>
            {flaggedProducts.map((line, idx) => (
              <div className={styles.analysisItem} key={`flag-${idx}`}>
                <span className={styles.summaryCheck} />
                <span className={styles.analysisText}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantCenterViewer;
